/**
 * SOLID Review
 * S — Good. One concern: the legal side's shells — the documents, the re-acceptance gate, the GDPR acts —
 *     each as at most one server call around the Domain's rules. No rule lives here.
 * O — Good. A new legal act is one more shell of the same shape.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Exactly the legal ops of `ClientUseCases`.
 * D — Strong. Two port interfaces, the account's sign-out and the shared steps, all injected.
 */
import type { IPortClientDecisions } from '../../domain/ports/ClientDecisions.contract';
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { ICommandNothing } from '../../domain/ports/schemas/TableMoves';
import type {
  IPayloadLegalDocuments, IEntityLegalReview, ICommandLegalAcceptance, IPayloadDataExport, ICommandDeleteAccount,
  ICommandPlanLegalReview, ICommandLegalReviewOf,
} from '../../domain/ports/schemas/LegalMoves';
import type { IPortClientLegalServer, IPortClientConsentPlatform } from '../ports/contracts';
import type {
  IPayloadConsentState, ICommandConsentChanged, ICommandCookieConsentRecord, ICommandCookiePreferences, IEntityPreferencesOpened,
} from '../../domain/ports/schemas/ConsentMoves';
import { DONE, type ClientSteps, type TOutcome, type TResult } from '../shared/ClientSteps';
import type { IServiceSignOut } from '../auth/AuthContext';

export interface IConfigDepLegalContext {
  readonly decisions: IPortClientDecisions;
  readonly legal: IPortClientLegalServer;
  /** The consent management platform (FE4b). */
  readonly consent: IPortClientConsentPlatform;
  /** The account's sign-out — a deleted account is a signed-out tab. */
  readonly account: IServiceSignOut;
  /** The one serial section, shared with the table and the session. */
  readonly steps: ClientSteps;
}

const NOTHING: ICommandNothing = {};
const NO_REVIEW: IEntityLegalReview = { review: [] };

/**
 * THE LEGAL SIDE'S SHELLS (frontend strict-ports plan, FE4a, 2026-10-04). The legal modal's tabs, the GDPR
 * export and deletion, and the re-acceptance gate called the legal REST adapter from React, and the gate
 * decided inline. The calls are here now; the gate's rules are the Domain's (`legalReview.ts`).
 *
 * THE GATE FAILS OPEN, as it did: a status or document read that fails reads as "nothing to accept" —
 * a transient error must not lock a player out of the game.
 */
export class LegalContext {
  constructor(private readonly deps: IConfigDepLegalContext) {}

  async loadLegalDocuments(ctx: IContextRequest, _command: ICommandNothing): Promise<TResult<IPayloadLegalDocuments>> {
    return this.deps.legal.documents(ctx);
  }

  async checkLegalReview(ctx: IContextRequest, _command: ICommandNothing): Promise<TResult<IEntityLegalReview>> {
    const { decisions, steps, legal } = this.deps;
    const status = await legal.consentStatus(ctx);
    if (!status.success) return { success: true, value: NO_REVIEW };
    const planned: ICommandPlanLegalReview = { status: status.value };
    const plan = await steps.session(ctx, (input) => decisions.planLegalReview(ctx, input, planned));
    if (!plan.success) return plan;
    if (!plan.value.fetchDocuments) return { success: true, value: NO_REVIEW };
    const documents = await legal.documents(ctx);
    if (!documents.success) return { success: true, value: NO_REVIEW };
    const loaded: ICommandLegalReviewOf = { documents: documents.value.documents };
    return steps.session(ctx, (input) => decisions.legalReviewOf(ctx, input, loaded));
  }

  /** The gate's Accept: Terms, then Privacy; the first that fails stops the rest, and says why. */
  async acceptLegalTerms(ctx: IContextRequest, command: ICommandLegalAcceptance): Promise<TOutcome> {
    const { decisions, steps, legal } = this.deps;
    const plan = await steps.session(ctx, (input) => decisions.planLegalAcceptance(ctx, input, command));
    if (!plan.success) return plan;
    for (const consent of plan.value.consents) {
      const recorded = await legal.recordConsent(ctx, consent);
      if (!recorded.success) return recorded;
    }
    return DONE;
  }

  async exportMyData(ctx: IContextRequest, _command: ICommandNothing): Promise<TResult<IPayloadDataExport>> {
    return this.deps.legal.exportMyData(ctx);
  }

  async requestAccountDeletion(ctx: IContextRequest, _command: ICommandNothing): Promise<TOutcome> {
    return this.deps.legal.requestAccountDeletionCode(ctx);
  }

  // ── cookie consent (FE4b) ───────────────────────────────────────────────────────────────────────

  /** Boot the consent platform (idempotent at the platform: StrictMode mounts the provider twice). */
  async startConsent(ctx: IContextRequest, _command: ICommandNothing): Promise<TOutcome> {
    return this.deps.consent.start(ctx);
  }

  async openCookiePreferences(ctx: IContextRequest, _command: ICommandNothing): Promise<TResult<IEntityPreferencesOpened>> {
    return this.deps.consent.openPreferences(ctx);
  }

  async saveCookiePreferences(ctx: IContextRequest, command: ICommandCookiePreferences): Promise<TOutcome> {
    return this.deps.consent.applyManualConsent(ctx, command);
  }

  /**
   * The CMP reported a state (delivered): the session keeps it; a resolved decision the server has not
   * heard of is recorded, at the Cookie Policy version the server publishes. The record is best-effort, as
   * it was (`.catch(() => undefined)`): consent works for the browser whether or not the audit landed.
   */
  async consentChanged(ctx: IContextRequest, state: IPayloadConsentState): Promise<TOutcome> {
    const { decisions, steps, legal } = this.deps;
    const changed: ICommandConsentChanged = { state };
    const kept = await steps.session(ctx, (input) => decisions.consentChanged(ctx, input, changed));
    if (!kept.success) return kept;
    if (!kept.value.record) return DONE;
    const documents = await legal.documents(ctx);
    const published = documents.success ? documents.value.documents.map((d) => ({ type: d.type, version: d.version })) : [];
    const planned: ICommandCookieConsentRecord = { state, published };
    const record = await steps.session(ctx, (input) => decisions.planCookieConsentRecord(ctx, input, planned));
    if (!record.success) return record;
    await legal.recordCookieConsent(ctx, record.value.request);
    return DONE;
  }

  /** The account deleted: the tab signs out — the same sign-out as Log out. */
  async deleteAccount(ctx: IContextRequest, command: ICommandDeleteAccount): Promise<TOutcome> {
    const deleted = await this.deps.legal.deleteAccount(ctx, command);
    if (!deleted.success) return deleted;
    return this.deps.account.signOut(ctx, NOTHING);
  }
}
